// =====================================================
// DASHBOARD AGROPECUARIO - VERSIÓN FINAL
// src/pages/Agro/Dashboard/index.js
// =====================================================
// Adaptado a las respuestas reales del backend:
// - Dashboard: totalProductores, totalEstablecimientos, etc.
// - Granos: precioArsTn (no precioUsdTn)
// - Hacienda: puede estar vacío []
// =====================================================

import React, { useState, useEffect } from "react"

import RequireRole from "components/RequireRole"
import {
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap"
import { useHistory } from "react-router-dom"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts"
import DataTable from "react-data-table-component"

import Layout from "components/Layout"
import { getDashboard, getPreciosGranos, getValoresGanaderia } from "api/agro"
import PanelAlertas from "pages/Agro/Alertas/PanelAlertas"
import toastHandler from "toastHandler"
import { getErrorMessage } from "helpers/error_helpers"
import { formatearNumero } from "helpers/formatters"

const SCORE_COLORS = {
  A: "#28a745",
  B: "#5cb85c",
  C: "#ffc107",
  D: "#fd7e14",
  E: "#dc3545",
}

// BDSJEA-524: campo del DTO del dashboard con error propio (se muestra solo en su tarjeta).
const CAMPO_SIN_SCORING = "productoresSinScoring"
const MENSAJE_PARCIAL_POR_DEFECTO =
  "No se pudieron cargar algunos datos del dashboard. Reintentá más tarde."
const MENSAJE_SIN_SCORING_POR_DEFECTO =
  "No se pudo calcular la cantidad de productores sin scoring."

const mensajeDeError = (item, porDefecto) =>
  typeof item.message === "string" && item.message.trim() !== ""
    ? item.message
    : porDefecto

// Helper: Asegura que el valor sea un array
const ensureArray = data => {
  if (data == null) return []
  if (Array.isArray(data)) return data
  if (data.data && Array.isArray(data.data)) return data.data
  if (data.content && Array.isArray(data.content)) return data.content
  console.warn("ensureArray: datos no reconocidos", typeof data)
  return []
}

const DashboardAgro = () => {
  const history = useHistory()
  const permissionBy = "agro.dashboard"
  const setError = toastHandler(state => state.setError)

  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState(null)
  const [preciosGranos, setPreciosGranos] = useState([])
  // BDSJEA-137: la card de hacienda pasa a mostrar los valores parametricos
  // del banco (Administracion / Valores Ganaderia, precio por kilo en USD)
  // en lugar del scraping del Mercado Agroganadero en pesos. Es el mismo dato
  // que usa el modelo de proyeccion, asi que el dashboard y el ITA muestran
  // el mismo precio.
  const [valoresGanaderia, setValoresGanaderia] = useState([])
  const [modalAlertas, setModalAlertas] = useState(false)
  // BDSJEA-524: motivo (mensaje del backend o por tipo de error) si getDashboard rechaza.
  const [errorDashboard, setErrorDashboard] = useState("")

  useEffect(() => {
    let isMounted = true

    const loadData = async () => {
      setLoading(true)
      let motivoDashboard = ""
      try {
        const [dashData, granosResponse, ganaderiaResponse] = await Promise.all(
          [
            getDashboard().catch(err => {
              // "" si fue una cancelacion: no se avisa nada.
              motivoDashboard = getErrorMessage(err)
              return null
            }),
            getPreciosGranos().catch(err => {
              console.warn("Error cargando precios granos:", err)
              return []
            }),
            getValoresGanaderia().catch(err => {
              console.warn("Error cargando valores de ganadería:", err)
              return []
            }),
          ],
        )

        if (isMounted) {
          setDashboard(dashData)
          setErrorDashboard(motivoDashboard)
          setPreciosGranos(ensureArray(granosResponse))
          setValoresGanaderia(ensureArray(ganaderiaResponse))
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error general en loadData:", error)
          setError("Error al cargar dashboard: " + error.message)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [])

  // BDSJEA-524: el backend responde siempre 200; los fallos parciales vienen en
  // erroresParciales (null/ausente/[] en lo normal). Lista segura: ignora
  // ítems nulos o malformados.
  const erroresParciales = Array.isArray(dashboard?.erroresParciales)
    ? dashboard.erroresParciales.filter(e => e && typeof e === "object")
    : []
  const errorSinScoring = erroresParciales.find(
    e => e.campo === CAMPO_SIN_SCORING,
  )
  // El error de "sin scoring" va solo en su tarjeta (el backend ya lo suma a
  // las alertas de sistema); el resto (dashboard y campos desconocidos) es aviso de página.
  const erroresDePagina = erroresParciales.filter(
    e => e.campo !== CAMPO_SIN_SCORING,
  )
  const dashboardFallido =
    dashboard == null || erroresParciales.some(e => e.campo === "dashboard")

  // KPIs Cards
  const KpiCards = () => {
    const sinScoring = dashboard?.productoresSinScoring
    const kpiSinScoring = {
      titulo: "Productores sin scoring",
      icono: "mdi-account-alert",
      color: "danger",
    }
    if (errorSinScoring) {
      kpiSinScoring.valor = "Dato no disponible"
      kpiSinScoring.detalle = mensajeDeError(
        errorSinScoring,
        MENSAJE_SIN_SCORING_POR_DEFECTO,
      )
    } else if (dashboardFallido || typeof sinScoring !== "number") {
      kpiSinScoring.valor = "-"
    } else {
      kpiSinScoring.valor = formatearNumero(sinScoring)
    }

    const kpis = [
      {
        titulo: "Productores",
        valor: dashboardFallido ? "-" : (dashboard.totalProductores ?? 0),
        icono: "mdi-account-group",
        color: "primary",
        link: "/agro/busqueda/productor",
      },
      // BDSJEA-133: la tarjeta "Establecimientos" se saca de la cabecera.
      // El backend sigue devolviendo totalEstablecimientos en el DTO del
      // dashboard; solo deja de mostrarse aca. El acceso a la busqueda de
      // establecimientos sigue disponible desde el menu.
      {
        titulo: "Superficie Total",
        valor: dashboardFallido
          ? "-"
          : dashboard.superficieTotalHa
            ? formatearNumero(dashboard.superficieTotalHa, 2)
            : "0",
        icono: "mdi-map-marker-radius",
        color: "info",
        sufijo: " ha",
      },
      {
        titulo: "Stock Ganadero",
        valor: dashboardFallido
          ? "-"
          : dashboard.stockGanaderoTotal
            ? formatearNumero(dashboard.stockGanaderoTotal, 0)
            : "0",
        icono: "mdi-cow",
        color: "warning",
        sufijo: " cab",
      },
      kpiSinScoring,
    ]

    return (
      <Row className="mb-3">
        {kpis.map((kpi, idx) => (
          <Col md={3} sm={6} key={idx} className="mb-2">
            <Card
              className="mini-stats-wid h-100"
              style={{ cursor: kpi.link ? "pointer" : "default" }}
              onClick={() => kpi.link && history.push(kpi.link)}
            >
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2">{kpi.titulo}</p>
                    <h4 className={kpi.detalle ? "mb-0 font-size-16" : "mb-0"}>
                      {kpi.valor}
                      {kpi.valor === "-" || kpi.detalle ? "" : kpi.sufijo || ""}
                    </h4>
                    {kpi.detalle && (
                      <small className="text-muted d-block mt-1">
                        {kpi.detalle}
                      </small>
                    )}
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span
                      className={`avatar-title rounded-circle bg-${kpi.color} bg-soft`}
                    >
                      <i
                        className={`mdi ${kpi.icono} text-${kpi.color} font-size-24`}
                      />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    )
  }

  // Distribución por Score
  const DistribucionScore = () => {
    const distribucion = dashboard?.distribucionScore
    const tieneData =
      distribucion && Object.values(distribucion).some(v => v > 0)

    const scoreData =
      distribucion && typeof distribucion === "object"
        ? Object.entries(distribucion).map(([cat, cant]) => ({
            name: `Cat. ${cat}`,
            value: cant || 0,
            color: SCORE_COLORS[cat] || "#999",
          }))
        : []

    return (
      <Card className="h-100">
        <CardBody>
          <h6 className="card-title">Distribución por Categoría de Riesgo</h6>
          {tieneData ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={scoreData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {scoreData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={value => `${value} productores`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-4">
              <i
                className="mdi mdi-chart-pie text-muted"
                style={{ fontSize: "3rem" }}
              />
              <p className="text-muted mt-2 mb-0">
                No hay productores con scoring calculado
              </p>
              <small className="text-muted">
                Agregá productores y calculá su scoring para ver la distribución
              </small>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  // Precios Granos - Usa precioArsTn
  const PreciosGranosCard = () => {
    const columns = [
      {
        name: "Cultivo",
        selector: row => row.cultivo || row.codigoCultivo || "-",
        sortable: true,
      },
      {
        name: "Precio $/tn",
        selector: row => row.precioArsTn,
        right: true,
        format: row => {
          const precio = row.precioArsTn || row.precioUsdTn
          if (!precio) return "-"
          return `$ ${Number(precio).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`
        },
      },
      {
        name: "Fuente",
        selector: row => row.fuente || "BCR",
        center: true,
        width: "120px",
      },
    ]

    const dataToShow = preciosGranos.slice(0, 6)

    return (
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title mb-0">
              <i className="mdi mdi-seed me-2 text-success" />
              Precios Granos
            </h6>
            <Badge color="light" className="text-dark">
              {preciosGranos[0]?.fecha || "Hoy"}
            </Badge>
          </div>
          {dataToShow.length > 0 ? (
            <DataTable
              columns={columns}
              data={dataToShow}
              dense
              noHeader
              noDataComponent={<small className="text-muted">Sin datos</small>}
            />
          ) : (
            <div className="text-center py-3">
              <Spinner size="sm" color="secondary" />
              <small className="text-muted ms-2">Cargando precios...</small>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  /**
   * BDSJEA-137 - Card de Precios de Hacienda del Dashboard.
   *
   * Antes tomaba el scraping del Mercado Agroganadero (precio y rango en ARS
   * por kilo) y repetia cinco veces la categoria NOVILLO porque la fuente
   * publica una fila por operatoria. Ahora muestra la parametria del banco:
   * Administracion / Valores Ganaderia, una fila por tipo de hacienda con el
   * precio por kilo en dolares. Es el valor que efectivamente entra en la
   * proyeccion, de modo que el dashboard deja de mostrar un numero distinto
   * al que usa el analisis.
   */
  const PreciosHaciendaCard = () => {
    const data = Array.isArray(valoresGanaderia) ? valoresGanaderia : []

    const columns = [
      {
        name: "Categoria",
        selector: row => row.tipoHacienda,
        sortable: true,
        grow: 2,
        cell: row => row.tipoHacienda || "-",
      },
      {
        name: "Precio Kg (USD)",
        selector: row => row.precioKilo,
        right: true,
        cell: row => {
          const precio = row.precioKilo
          if (precio == null) return "-"
          return (
            <strong className="text-success">
              U$S{" "}
              {Number(precio).toLocaleString("es-AR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          )
        },
      },
    ]

    const estilosTabla = {
      headCells: {
        style: {
          whiteSpace: "normal",
          overflow: "visible",
          textOverflow: "clip",
        },
      },
    }

    const Cabecera = () => (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="card-title mb-0">
          <i className="mdi mdi-cow me-2 text-warning" />
          Precios Hacienda
        </h6>
      </div>
    )

    if (data.length === 0) {
      return (
        <Card>
          <CardBody>
            <Cabecera />
            <div className="text-center py-4">
              <i
                className="mdi mdi-cow-off text-muted"
                style={{ fontSize: "2rem", opacity: 0.5 }}
              />
              <p className="text-muted mt-2 mb-0">
                <small>Sin valores de ganaderia cargados</small>
              </p>
            </div>
          </CardBody>
        </Card>
      )
    }

    return (
      <Card>
        <CardBody>
          <Cabecera />
          <DataTable
            columns={columns}
            data={data}
            customStyles={estilosTabla}
            dense
            noHeader
            noDataComponent={<small className="text-muted">Sin datos</small>}
          />
          <div className="mt-2 text-end">
            <small className="text-muted">
              Fuente: Administracion / Valores Ganaderia
            </small>
          </div>
        </CardBody>
      </Card>
    )
  }

  // Helper: color del badge según nivel de alerta
  // Convive la escala vieja (ALERTA/AVISO, de las alertas de scoring y de
  // sistema) con la escala de severidad de alerta_climatica (CRITICO/ALTO/
  // MEDIO/BAJO), que espeja los colores del SAT del SMN. Sin las claves
  // nuevas un alerta roja del SMN caia en el "info" por defecto y se veia
  // celeste, igual que un aviso informativo.
  const alertaBadgeColor = nivel => {
    if (!nivel) return "info"
    const n = nivel.toUpperCase()
    if (n === "ALERTA" || n === "CRITICAL" || n === "CRITICO") return "danger"
    if (n === "AVISO" || n === "WARNING" || n === "ALTO" || n === "MEDIO")
      return "warning"
    return "info"
  }

  // Helper: icono según tipo de alerta
  const alertaIcono = titulo => {
    if (!titulo) return "mdi-alert-circle-outline"
    const t = titulo.toUpperCase()
    if (t.includes("SEQUIA") || t.includes("HIDRIC")) return "mdi-water-off"
    if (t.includes("HELADA")) return "mdi-snowflake"
    // Tipos que emite el ingestor CAP del SMN
    if (t.includes("ZONDA") || t.includes("VIENTO")) return "mdi-weather-windy"
    if (t.includes("GRANIZO")) return "mdi-weather-hail"
    if (t.includes("TORMENTA")) return "mdi-weather-lightning-rainy"
    if (t.includes("LLUVIA")) return "mdi-weather-pouring"
    if (t.includes("NEVADA") || t.includes("NIEVE"))
      return "mdi-weather-snowy-heavy"
    if (t.includes("TEMP_EXTREMA") || t.includes("TEMPERATURA"))
      return "mdi-thermometer"
    if (t.includes("EXCESO") || t.includes("ANEGAMIENTO")) return "mdi-waves"
    if (t.includes("NDVI")) return "mdi-leaf"
    if (t.includes("SEGURO")) return "mdi-shield-off-outline"
    if (t.includes("MONOCULTIVO") || t.includes("DIVERSIFICACION"))
      return "mdi-sprout"
    if (t.includes("MARGEN") || t.includes("INSUFICIENTE"))
      return "mdi-currency-usd-off"
    if (
      t.includes("SCORING") ||
      t.includes("EVALUACION") ||
      t.includes("VENCIDO")
    )
      return "mdi-chart-box-outline"
    return "mdi-alert-circle-outline"
  }

  // Alertas - Card resumen con click para ver modal completo
  const AlertasCard = () => {
    const alertas = ensureArray(dashboard?.alertasClimaticas)
    const pendientes = dashboard?.alertasPendientes ?? 0
    const preview = alertas.slice(0, 4)

    return (
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title mb-0">
              <i className="mdi mdi-bell-outline me-2" />
              Alertas del sistema
            </h6>
            <Badge color={pendientes > 0 ? "danger" : "secondary"} pill>
              {pendientes}
            </Badge>
          </div>
          {alertas.length > 0 ? (
            <>
              {preview.map((alerta, idx) => (
                <div
                  key={idx}
                  className="d-flex align-items-start py-2"
                  style={{
                    borderBottom:
                      idx < preview.length - 1 ? "1px solid #eee" : "none",
                    cursor: "pointer",
                  }}
                  onClick={() => setModalAlertas(true)}
                >
                  <div className="me-2 mt-1">
                    <i
                      className={`mdi ${alertaIcono(alerta.titulo)} text-${alertaBadgeColor(alerta.nivel)} font-size-16`}
                    />
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="d-flex align-items-center mb-1">
                      <Badge
                        color={alertaBadgeColor(alerta.nivel)}
                        className="me-2"
                        style={{ fontSize: "0.65rem" }}
                      >
                        {alerta.nivel || "INFO"}
                      </Badge>
                      <small className="fw-bold text-truncate">
                        {alerta.titulo || "Alerta"}
                      </small>
                    </div>
                    <small className="text-muted d-block text-truncate">
                      {alerta.descripcion || ""}
                    </small>
                    {alerta.zona && (
                      <small
                        className="text-muted"
                        style={{ fontSize: "0.7rem" }}
                      >
                        <i className="mdi mdi-map-marker-outline me-1" />
                        {alerta.zona}
                      </small>
                    )}
                  </div>
                </div>
              ))}
              {alertas.length > 4 && (
                <div className="text-center mt-2">
                  <button
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    onClick={() => setModalAlertas(true)}
                  >
                    <i className="mdi mdi-eye-outline me-1" />
                    Ver las {alertas.length} alertas
                  </button>
                </div>
              )}
              {alertas.length <= 4 && (
                <div className="text-center mt-2">
                  <button
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    onClick={() => setModalAlertas(true)}
                  >
                    <i className="mdi mdi-open-in-new me-1" />
                    Ver detalle
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-3">
              <i
                className="mdi mdi-check-circle text-success"
                style={{ fontSize: "2rem" }}
              />
              <p className="text-muted mt-2 mb-0">
                <small>Sin alertas pendientes</small>
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  // Modal con lista completa de alertas
  const ModalAlertas = () => {
    const alertas = ensureArray(dashboard?.alertasClimaticas)

    // Agrupar por nivel para mostrar las más graves primero
    const alertasOrdenadas = [...alertas].sort((a, b) => {
      const orden = {
        ALERTA: 0,
        CRITICAL: 0,
        AVISO: 1,
        WARNING: 1,
        INFORMACION: 2,
        INFO: 2,
      }
      return (
        (orden[a.nivel?.toUpperCase()] ?? 3) -
        (orden[b.nivel?.toUpperCase()] ?? 3)
      )
    })

    const countByNivel = alertas.reduce(
      (acc, a) => {
        const nivel = a.nivel?.toUpperCase() || "INFO"
        const key =
          nivel === "ALERTA" || nivel === "CRITICAL"
            ? "criticas"
            : nivel === "AVISO" || nivel === "WARNING"
              ? "avisos"
              : "info"
        acc[key] = (acc[key] || 0) + 1
        return acc
      },
      { criticas: 0, avisos: 0, info: 0 },
    )

    return (
      <Modal
        isOpen={modalAlertas}
        toggle={() => setModalAlertas(false)}
        size="lg"
        scrollable
      >
        <ModalHeader toggle={() => setModalAlertas(false)}>
          <i className="mdi mdi-bell-alert me-2" />
          Alertas del Sistema
          <Badge color="danger" pill className="ms-2">
            {alertas.length}
          </Badge>
        </ModalHeader>
        <ModalBody>
          {/* Resumen de conteo */}
          <div className="d-flex gap-3 mb-3 pb-3 border-bottom">
            <div className="d-flex align-items-center">
              <Badge color="danger" className="me-1">
                {countByNivel.criticas}
              </Badge>
              <small className="text-muted">Críticas</small>
            </div>
            <div className="d-flex align-items-center">
              <Badge color="warning" className="me-1">
                {countByNivel.avisos}
              </Badge>
              <small className="text-muted">Avisos</small>
            </div>
            <div className="d-flex align-items-center">
              <Badge color="info" className="me-1">
                {countByNivel.info}
              </Badge>
              <small className="text-muted">Informativas</small>
            </div>
          </div>

          {/* Lista de alertas */}
          {alertasOrdenadas.map((alerta, idx) => {
            const color = alertaBadgeColor(alerta.nivel)
            return (
              <div
                key={idx}
                className={`d-flex align-items-start p-3 mb-2 rounded border-start border-4 border-${color}`}
                style={{ backgroundColor: "var(--bs-light, #f8f9fa)" }}
              >
                <div className="me-3 mt-1">
                  <i
                    className={`mdi ${alertaIcono(alerta.titulo)} text-${color}`}
                    style={{ fontSize: "1.5rem" }}
                  />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-1">
                    <Badge
                      color={color}
                      className="me-2"
                      style={{ fontSize: "0.7rem" }}
                    >
                      {alerta.nivel || "INFO"}
                    </Badge>
                    <strong>{alerta.titulo || "Alerta"}</strong>
                  </div>
                  <p className="mb-1 text-muted small">
                    {alerta.descripcion || ""}
                  </p>
                  {alerta.zona && (
                    <small className="text-muted">
                      <i className="mdi mdi-map-marker-outline me-1" />
                      {alerta.zona}
                    </small>
                  )}
                </div>
              </div>
            )
          })}

          {alertasOrdenadas.length === 0 && (
            <div className="text-center py-4">
              <i
                className="mdi mdi-check-circle text-success"
                style={{ fontSize: "3rem" }}
              />
              <p className="text-muted mt-2">No hay alertas activas</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    )
  }

  // Accesos Rápidos
  const AccesosRapidos = () => (
    <Card>
      <CardBody>
        <h6 className="card-title">
          <i className="mdi mdi-lightning-bolt me-2" />
          Accesos Rápidos
        </h6>
        <div className="d-grid gap-2">
          <button
            className="btn btn-outline-primary btn-sm text-start"
            onClick={() => history.push("/agro/busqueda/productor")}
          >
            <i className="mdi mdi-magnify me-2" />
            Buscar Productor
          </button>
          <RequireRole objeto="/agro/busqueda/productor" accion="modificacion">
            <button
              className="btn btn-outline-success btn-sm text-start"
              onClick={() => history.push("/agro/alta-productor")}
            >
              <i className="mdi mdi-plus me-2" />
              Alta Productor
            </button>
          </RequireRole>
          {/* BDSJEA-136: /agro/precios ya no existe como ruta propia; Precios
              de Mercado es una solapa dentro de Administracion. */}
          <button
            className="btn btn-outline-info btn-sm text-start"
            onClick={() => history.push("/agro/admin")}
          >
            <i className="mdi mdi-currency-usd me-2" />
            Precios de Mercado
          </button>
        </div>
      </CardBody>
    </Card>
  )

  const MainContent = () =>
    loading ? (
      <div className="text-center py-5">
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p className="mt-3 text-muted">Cargando dashboard...</p>
      </div>
    ) : (
      <>
        {errorDashboard && (
          <Alert color="warning" className="py-1 mb-2">
            <small>{errorDashboard}</small>
          </Alert>
        )}
        {erroresDePagina.map((e, idx) => (
          <Alert color="warning" className="py-1 mb-2" key={idx}>
            <small>{mensajeDeError(e, MENSAJE_PARCIAL_POR_DEFECTO)}</small>
          </Alert>
        ))}
        <KpiCards />

        <Row>
          <Col lg={8}>
            <Row>
              <Col md={6} className="mb-3">
                <PreciosGranosCard />
              </Col>
              <Col md={6} className="mb-3">
                <PreciosHaciendaCard />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mb-3">
                <DistribucionScore />
              </Col>
            </Row>
          </Col>
          <Col lg={4}>
            <div className="mb-3">
              <AlertasCard />
            </div>
            <AccesosRapidos />
          </Col>
        </Row>

        {/* Alertas meteorologicas oficiales del SMN.
            Va en fila propia y a lo ancho porque cada alerta se despliega
            para mostrar la tabla de establecimientos y productores
            alcanzados, que no entra en la columna lateral. */}
        <Row>
          <Col md={12} className="mb-3">
            <Card>
              <CardBody>
                <h6 className="card-title">
                  <i className="mdi mdi-weather-lightning-rainy me-2" />
                  Alertas meteorologicas oficiales
                </h6>
                <PanelAlertas alcance="cartera" limite={5} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ModalAlertas />
      </>
    )

  return (
    <Layout
      components={[{ component: <MainContent /> }]}
      title="Dashboard Agropecuario"
      permissionBy={permissionBy}
    />
  )
}

export default DashboardAgro
